import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import LucideSymbolIcon from '../../components/icons/LucideSymbolIcon';
import PasswordRecoveryLayout, {
  AUTH_FIELD_CLASS,
  RecoveryAlert,
  RecoveryAuthField,
  RecoveryOutlineButton,
  RecoveryPrimaryButton,
} from '../../components/auth/PasswordRecoveryLayout';
import { requestPasswordReset, verifyPasswordResetCode } from '../../services/api';

function isValidEmail(value: string) {
  const v = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function VerifyPasswordResetPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qpEmail = (searchParams.get('email') || '').trim().toLowerCase();

  const [email, setEmail] = useState(qpEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendHint, setResendHint] = useState('');

  useEffect(() => {
    if (qpEmail) setEmail(qpEmail);
  }, [qpEmail]);

  const canSubmit = useMemo(
    () => isValidEmail(email) && code.trim().replace(/\s/g, '').length === 6,
    [email, code],
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setResendHint('');
    if (!canSubmit) {
      setError('Saisissez une adresse e-mail valide et le code à 6 caractères reçu par e-mail.');
      return;
    }
    setLoading(true);
    try {
      const token = await verifyPasswordResetCode(email.trim().toLowerCase(), code.trim());
      navigate(`/reset-password?token=${encodeURIComponent(token)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code invalide ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendHint('');
    if (!isValidEmail(email)) {
      setError('Adresse e-mail invalide pour renvoyer le code.');
      return;
    }
    setResendLoading(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      setResendHint('Si un compte existe pour cette adresse, un nouveau code vient d’être envoyé.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de renvoyer le code.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <PasswordRecoveryLayout
      activeStep={2}
      compact
      compactTopAlign
      title="Code reçu par e-mail"
      backLink={{ label: 'Retour à la connexion', to: '/login' }}
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
        <RecoveryAuthField label="Adresse e-mail" icon="mail" id="vr-email">
          <input
            id="vr-email"
            type="email"
            autoComplete="email"
            className={AUTH_FIELD_CLASS}
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="vous@exemple.cd"
          />
        </RecoveryAuthField>

        <RecoveryAuthField label="Code à 6 caractères" icon="verified_user" id="vr-code">
          <input
            id="vr-code"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={6}
            className={`${AUTH_FIELD_CLASS} font-mono tracking-[0.25em] uppercase`}
            value={code}
            onChange={(ev) => setCode(ev.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
            placeholder="A2B4C6"
          />
        </RecoveryAuthField>

        {error ? <RecoveryAlert variant="error">{error}</RecoveryAlert> : null}
        {resendHint ? <RecoveryAlert variant="success">{resendHint}</RecoveryAlert> : null}

        <p className="text-[11px] leading-snug text-on-surface-variant sm:text-xs">
          Le code est valable pour une durée limitée. Consultez le dossier spam si besoin.
        </p>

        <RecoveryPrimaryButton loading={loading} disabled={!canSubmit || loading}>
          {loading ? (
            'Vérification…'
          ) : (
            <>
              Continuer
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="text-white" />
            </>
          )}
        </RecoveryPrimaryButton>

        <RecoveryOutlineButton disabled={resendLoading} onClick={() => void handleResend()}>
          {resendLoading ? 'Envoi…' : 'Renvoyer le code'}
        </RecoveryOutlineButton>

        <p className="text-center text-[11px] sm:text-xs">
          <Link to="/forgot-password" className="font-semibold text-primary hover:underline">
            Changer d&apos;adresse e-mail
          </Link>
        </p>
      </form>
    </PasswordRecoveryLayout>
  );
}
